<template>
  <div class="sf-page">
    <top-header v-bind:title="$t('addCustomEmailTemplate')">
      <v-btn class="sf-back-btn"
             exact
             slot="left-button"
             text
             v-bind:to="{ name: 'administration-custom-email-templates' }">
        <v-icon left>ti-arrow-left</v-icon>
        {{ $t('back') }}
      </v-btn>
      <v-btn color="primary"
             depressed
             exact
             slot="right-button"
             v-bind:loading="creating"
             v-on:click="createCustomEmailTemplate">
        <v-icon left>ti-check</v-icon>
        {{ $t('add') }}
      </v-btn>
    </top-header>
    <div class="sf-container">
      <v-alert class="sf-alert"
               text
               type="error"
               v-model="errorAlert">
        {{ errorMessage || $t('pleaseCompleteTheForm') }}
      </v-alert>
      <v-row>
        <v-col cols="12"
               lg="8">
          <v-card class="sf-card"
                  flat>
            <div class="sf-card__header">
              <h2 class="sf-card__title">{{ $t('newTemplate') }}</h2>
            </div>
            <v-card-text class="sf-card__body">
              <v-form ref="form">
                <v-text-field dense class="mb-4"
                              hide-details="auto"
                              outlined
                              v-bind:label="$t('name')"
                              v-bind:rules="[ rules.required ]"
                              v-model="name" />
                <v-row>
                  <v-col cols="12"
                         md="6">
                    <v-select dense hide-details="auto"
                              outlined
                              v-bind:items="audiences"
                              v-bind:label="$t('audience')"
                              v-model="audience" />
                  </v-col>
                  <v-col cols="12"
                         md="6">
                    <v-select dense hide-details="auto"
                              outlined
                              v-bind:items="kinds"
                              v-bind:label="$t('kind')"
                              v-model="kind" />
                  </v-col>
                </v-row>

                <h3 class="sf-section-title mt-8">{{ $t('startFrom') }}</h3>
                <v-radio-group class="mt-0"
                               hide-details
                               v-model="source">
                  <v-radio class="sf-radio-card"
                           color="primary"
                           v-bind:key="layout.id"
                           v-bind:value="`layout:${layout.id}`"
                           v-for="layout in layouts">
                    <template v-slot:label>
                      <div>
                        <strong>{{ layout.name }}</strong>
                        <div class="text-caption sf-muted">{{ $t('layout') }} · {{ layout.modified | formatDate('L') }}</div>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio class="sf-radio-card"
                           color="primary"
                           value="blank">
                    <template v-slot:label>
                      <div>
                        <strong>{{ $t('blank') }}</strong>
                        <div class="text-caption sf-muted">{{ $t('blankHint') }}</div>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
                <p class="text-caption sf-muted mt-4 mb-0"
                   v-if="layouts.length === 0">
                  {{ $t('noLayouts') }}
                </p>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import TopHeader from '@/components/common/TopHeader'

  export const BLANK_MJML = `<mjml>
  <mj-body background-color="#f0f0f0">
    <mj-section background-color="#ffffff">
      <mj-column>
        <mj-text color="#e3000a" font-size="16px"><strong>Überschrift</strong></mj-text>
        <mj-text>{{salutation}},</mj-text>
        <mj-text>Hier Text einfügen …</mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`

  export default {
    components: {
      TopHeader
    },
    computed: {
      ...mapGetters([ 'customEmailTemplates' ]),
      audiences() {
        return [
          { text: this.$t('customers'), value: 'Customer' },
          { text: this.$t('suppliers'), value: 'Supplier' }
        ]
      },
      kinds() {
        return [
          { text: this.$t('kindEmail'), value: 'Email' },
          { text: this.$t('kindLayout'), value: 'Layout' }
        ]
      },
      layouts() {
        return this.customEmailTemplates.filter(t => t.kind === 'Layout' && t.audience === this.audience)
      },
      rules() {
        return {
          required: v => !!v || this.$t('form.required')
        }
      }
    },
    async created() {
      await this.$store.dispatch('loadCustomEmailTemplates')
      this.selectDefaultSource()
    },
    data: () => ({
      audience: 'Customer',
      creating: false,
      errorAlert: false,
      errorMessage: null,
      kind: 'Email',
      name: '',
      source: 'blank'
    }),
    methods: {
      selectDefaultSource() {
        this.source = this.layouts.length > 0 ? `layout:${this.layouts[0].id}` : 'blank'
      },
      async createCustomEmailTemplate() {
        this.errorAlert = false
        this.errorMessage = null

        if (!this.$refs.form.validate()) {
          this.errorAlert = true
          this.$vuetify.goTo(0)

          return
        }

        this.creating = true

        try {
          let customEmailTemplate

          if (this.source.startsWith('layout:')) {
            const id = parseInt(this.source.substring(7), 10)

            customEmailTemplate = await this.$store.dispatch('duplicateCustomEmailTemplate', { id, name: this.name, kind: this.kind })
          } else {
            customEmailTemplate = {
              name: this.name,
              title: this.name,
              audience: this.audience,
              kind: this.kind,
              mjml: BLANK_MJML
            }

            await this.$store.dispatch('createCustomEmailTemplate', { customEmailTemplate })
          }

          this.$router.push({ name: 'administration-custom-email-template', params: { id: customEmailTemplate.id } })
        } catch (e) {
          this.errorMessage = e.message
          this.errorAlert = true
          this.$vuetify.goTo(0)
        } finally {
          this.creating = false
        }
      }
    },
    name: 'AddCustomEmailTemplate',
    watch: {
      audience() {
        this.selectDefaultSource()
      }
    }
  }
</script>

<style scoped>
  .sf-radio-card {
    align-items: flex-start;
    margin-bottom: 12px;
  }
</style>

<i18n>
  {
    "de": {
      "add": "Anlegen",
      "addCustomEmailTemplate": "E-Mail-Vorlage anlegen",
      "back": "Zurück",
      "newTemplate": "Neue Vorlage",
      "name": "Name (intern)",
      "audience": "Zielgruppe",
      "customers": "Kunden",
      "suppliers": "Lieferanten",
      "kind": "Art",
      "kindEmail": "E-Mail",
      "kindLayout": "Layout (Startvorlage)",
      "startFrom": "Ausgangspunkt",
      "layout": "Layout",
      "blank": "Leere Vorlage",
      "blankHint": "Nur ein Textabschnitt, alles Weitere wird im Editor zusammengestellt.",
      "noLayouts": "Für diese Zielgruppe gibt es noch kein Layout. Eine beliebige E-Mail kann in der Liste über „Als Layout speichern“ zur Startvorlage werden.",
      "pleaseCompleteTheForm": "Bitte vervollständigen Sie Ihre Angaben."
    },
    "en": {
      "add": "Create",
      "addCustomEmailTemplate": "Create email template",
      "back": "Back",
      "newTemplate": "New template",
      "name": "Name (internal)",
      "audience": "Audience",
      "customers": "Customers",
      "suppliers": "Suppliers",
      "kind": "Kind",
      "kindEmail": "Email",
      "kindLayout": "Layout (starting template)",
      "startFrom": "Start from",
      "layout": "Layout",
      "blank": "Blank template",
      "blankHint": "A single text section, everything else is assembled in the editor.",
      "noLayouts": "There is no layout for this audience yet. Any email can become a layout via “Save as layout” in the list.",
      "pleaseCompleteTheForm": "Please complete the form."
    }
  }
</i18n>
